if (!customElements.get('buybox-widget')) {
  class BuyboxWidget extends HTMLElement {
    constructor() {
      super();
      this.tiers = [];
      this.activeTierIndex = -1;
      this.discountApplied = false;
      // Refs we capture when we take over the product form so we can
      // restore them in disconnectedCallback (matters for quick-view modals
      // that reuse the same DOM).
      this._hiddenButtonsEl = null;
      this._variantUnsubscriber = null;
    }

    connectedCallback() {
      this.tiersRaw = (this.dataset.tiers || '').trim();
      this.productId = parseInt(this.dataset.productId || '0', 10);
      this.variantId = parseInt(this.dataset.variantId || '0', 10);
      this.productPrice = parseInt(this.dataset.productPrice || '0', 10);
      this.heading = this.dataset.heading || 'Exclusive Savings';
      this.discountCode = (this.dataset.discountCode || '').trim();
      this.bestValueIndex = parseInt(this.dataset.bestValue || '-1', 10);
      this.productFormId = (this.dataset.productFormId || '').trim();
      this.sectionId = (this.dataset.sectionId || '').trim();
      this.replaceAtc = this.dataset.replaceAtc === 'true';
      this.moneyFormat = window.FoxTheme && FoxTheme.settings ? FoxTheme.settings.moneyFormat : '${{amount}}';

      if (!this.tiersRaw || !this.productPrice) {
        this.innerHTML = '';
        this.setAttribute('hidden', '');
        return;
      }

      this.parseTiers();

      if (this.tiers.length === 0) {
        this.innerHTML = '';
        this.setAttribute('hidden', '');
        return;
      }

      this.activeTierIndex = this.bestValueIndex >= 0 && this.bestValueIndex < this.tiers.length
        ? this.bestValueIndex
        : 0;

      this.render();

      // After successful render the buybox owns the purchase flow:
      // hide the theme's standard quantity selector + ATC + Shop Pay
      // button so the customer has exactly one purchase CTA on the page.
      if (this.replaceAtc) {
        this.takeoverBuyButtons();
      }

      // Refresh prices when the customer picks a different variant
      // (color/size). Without this the buybox shows the first variant's
      // price forever, even after the URL/variant changes.
      this.subscribeToVariantChange();
    }

    disconnectedCallback() {
      // Restore the standard buy buttons we hid. Important in quick-view
      // modals where the same DOM gets reused for different products.
      if (this._hiddenButtonsEl) {
        this._hiddenButtonsEl.removeAttribute('data-buybox-hidden');
        this._hiddenButtonsEl.style.display = '';
        this._hiddenButtonsEl = null;
      }
      if (typeof this._variantUnsubscriber === 'function') {
        this._variantUnsubscriber();
        this._variantUnsubscriber = null;
      }
    }

    // ---- Takeover helpers ----------------------------------------------
    findProductForm() {
      // Prefer the explicit form id we got from Liquid; falls back to the
      // single product form on the page (good enough for non-quick-view
      // contexts).
      if (this.productFormId) {
        const byId = document.getElementById(this.productFormId);
        if (byId) return byId;
      }
      return document.querySelector('form.main-product-form, form[action$="/cart/add"]');
    }

    takeoverBuyButtons() {
      const form = this.findProductForm();
      if (!form) return;
      // .product-form__buttons is the wrapper that contains the
      // quantity-input, the submit button, and the dynamic checkout
      // (Shop Pay / Apple Pay) button. Hiding the wrapper kills all
      // three in one shot, which is the desired "real buybox" UX.
      const buttonsEl = form.querySelector('.product-form__buttons');
      if (!buttonsEl) return;
      buttonsEl.setAttribute('data-buybox-hidden', 'true');
      buttonsEl.style.display = 'none';
      this._hiddenButtonsEl = buttonsEl;
    }

    subscribeToVariantChange() {
      if (!(window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS)) return;
      this._variantUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        (event) => {
          const v = event && event.data && event.data.variant;
          if (!v) return;
          // Filter to events for the form we're attached to. Without this
          // a quick-view variant change could redraw the PDP buybox.
          if (this.sectionId && event.data.sectionId && event.data.sectionId !== this.sectionId) {
            return;
          }
          this.variantId = v.id;
          if (typeof v.price === 'number' && v.price > 0) {
            this.productPrice = v.price;
            // Re-render in place so all tier prices reflect the new
            // variant. Preserves the active tier selection.
            this.render();
          }
        }
      );
    }

    parseTiers() {
      const entries = this.tiersRaw.split(',').map((s) => s.trim()).filter(Boolean);
      this.tiers = entries.map((entry) => {
        const parts = entry.split(':');
        const qty = parseInt(parts[0], 10) || 1;
        const type = (parts[1] || 'percent').trim().toLowerCase();
        const value = parseFloat(parts[2]) || 0;
        const label = parts.length > 3 ? parts.slice(3).join(':').trim() : '';
        return { qty, type, value, label };
      }).filter((t) => t.qty > 0);
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.round(cents / 100).toString();
      const amountWithComma = amount.replace('.', ',');

      let formatted = this.moneyFormat;
      formatted = formatted.replace('{{amount_with_comma_separator}}', amountWithComma);
      formatted = formatted.replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
      formatted = formatted.replace('{{amount_no_decimals}}', amountNoDecimals);
      formatted = formatted.replace('{{amount}}', amount);
      return formatted;
    }

    getTierPricing(tier) {
      const unitPrice = this.productPrice;
      const originalTotal = unitPrice * tier.qty;

      if (tier.type === 'percent') {
        const discountedUnit = unitPrice - Math.floor(unitPrice * tier.value / 100);
        const discountedTotal = discountedUnit * tier.qty;
        return {
          originalTotal,
          discountedTotal,
          savings: originalTotal - discountedTotal,
          unitPrice: discountedUnit,
          freeQty: 0,
          paidQty: tier.qty,
        };
      }

      if (tier.type === 'free') {
        const freeQty = Math.floor(tier.value) || 1;
        const paidQty = tier.qty - freeQty;
        const discountedTotal = Math.max(0, paidQty) * unitPrice;
        return {
          originalTotal,
          discountedTotal,
          savings: originalTotal - discountedTotal,
          unitPrice,
          freeQty,
          paidQty: Math.max(0, paidQty),
        };
      }

      if (tier.type === 'fixed') {
        const discountedTotal = Math.max(0, originalTotal - Math.round(tier.value * 100));
        return {
          originalTotal,
          discountedTotal,
          savings: originalTotal - discountedTotal,
          unitPrice,
          freeQty: 0,
          paidQty: tier.qty,
        };
      }

      return {
        originalTotal,
        discountedTotal: originalTotal,
        savings: 0,
        unitPrice,
        freeQty: 0,
        paidQty: tier.qty,
      };
    }

    // ---- Tier labels & promo strings -----------------------------------
    // Note on zero-discount tiers: a tier is "baseline" when its discount
    // value is 0 OR the computed savings are 0. Baselines should NOT be
    // dressed up with "Save 0%" / "Exclusive 0% savings" language — that
    // erodes trust in the promotion. We return plain labels in that case
    // and let the discounted tiers do the persuasive lifting.

    isBaselineTier(tier) {
      // A tier is considered baseline if its discount value is 0 or missing.
      // For 'free' type, value = number of free units; 0 = no gift.
      // For 'percent'/'fixed', value = the discount amount; 0 = no discount.
      return !tier.value || tier.value <= 0;
    }

    getTierLabel(tier) {
      // Custom label from the metafield wins. E.g. `4:free:2:Family Pack`
      // outputs "Family Pack" verbatim — useful when merchants want a
      // brand-voice label instead of the generated one.
      if (tier.label) return tier.label;

      // Baseline / no-discount → just "Single" or "Buy N".
      if (this.isBaselineTier(tier)) {
        return tier.qty === 1 ? 'Single' : `Buy ${tier.qty}`;
      }

      if (tier.type === 'percent') return `Buy ${tier.qty}`;

      // "Buy X Get Y" — where X = paid qty, Y = TOTAL qty (paid + free).
      // This is the standard BOGO marketing phrasing and what the
      // customer actually walks away with.
      //   `6:free:2`  → "Buy 4 Get 6"   (pay for 4, take 6)
      //   `4:free:1`  → "Buy 3 Get 4"   (pay for 3, take 4)
      //   `2:free:1`  → "Buy 1 Get 2"   (classic BOGO free)
      if (tier.type === 'free') {
        const freeQty = Math.floor(tier.value);
        const paidQty = Math.max(0, tier.qty - freeQty);
        return `Buy ${paidQty} Get ${tier.qty}`;
      }

      if (tier.type === 'fixed') return `Buy ${tier.qty}`;
      return `Buy ${tier.qty}`;
    }

    getTierDescription(tier) {
      // Per-piece price acts as the "description" line — it's the most
      // useful comparison number for the customer ("$X.XX each"). We only
      // show it for multi-quantity tiers; single-item baseline tiers
      // get nothing here (the total IS the per-piece, redundant).
      if (tier.qty <= 1) return '';
      const tp = this.getTierPricing(tier);
      const perUnitCents = Math.round(tp.discountedTotal / tier.qty);
      return `${this.formatMoney(perUnitCents)} each`;
    }

    // Compact "SAVE X%" / "+N FREE" / "SAVE $Y" — used in the floating
    // promo pill on each discounted tier card.
    getSavingsLabel(tier, tp) {
      if (this.isBaselineTier(tier) || tp.savings <= 0) return '';
      if (tier.type === 'percent') return `Save ${tier.value}%`;
      if (tier.type === 'free') {
        const freeQty = Math.floor(tier.value);
        return `+${freeQty} Free`;
      }
      if (tier.type === 'fixed') return `Save ${this.formatMoney(tp.savings)}`;
      return '';
    }

    // Header subtitle: dynamic call-out based on the strongest tier on
    // the page. E.g. with tiers [5%, 15%, 25%] → "Save up to 25%".
    getMaxDiscountLabel() {
      let maxPercent = 0;
      let maxDollars = 0;
      this.tiers.forEach((tier) => {
        if (this.isBaselineTier(tier)) return;
        if (tier.type === 'percent' && tier.value > maxPercent) {
          maxPercent = tier.value;
        } else if (tier.type === 'free' && tier.value > 0) {
          // Approximate free-tier % using qty / value.
          const pct = Math.round((tier.value / tier.qty) * 100);
          if (pct > maxPercent) maxPercent = pct;
        } else if (tier.type === 'fixed' && tier.value > maxDollars) {
          maxDollars = tier.value;
        }
      });
      if (maxPercent > 0) return `Save up to ${maxPercent}%`;
      if (maxDollars > 0) return `Save up to ${this.formatMoney(maxDollars * 100)}`;
      return 'More you select, more you save';
    }

    render() {
      const activeTier = this.tiers[this.activeTierIndex];
      const pricing = this.getTierPricing(activeTier);

      let html = `<div class="bb-wrapper">`;

      html += `<div class="bb-header">`;
      html += `<div class="bb-header__icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg></div>`;
      html += `<div class="bb-header__text"><h3 class="bb-header__title">${this.escapeHtml(this.heading)}</h3>`;
      // Header subtitle is now a dynamic promo line ("Save up to 25%")
      // computed from the strongest tier; falls back to a neutral string
      // if no tier carries a discount.
      html += `<p class="bb-header__subtitle">${this.escapeHtml(this.getMaxDiscountLabel())}</p></div>`;
      html += `</div>`;

      html += `<ul class="bb-tiers">`;
      this.tiers.forEach((tier, i) => {
        const tp = this.getTierPricing(tier);
        const isActive = i === this.activeTierIndex;
        const isBest = i === this.bestValueIndex;
        const isBaseline = this.isBaselineTier(tier) || tp.savings <= 0;
        const savingsLabel = this.getSavingsLabel(tier, tp);

        // Per-tier classes drive the entirely different visual treatment
        // of baseline vs discounted in CSS.
        const tierClassList = [
          'bb-tier',
          isBaseline ? 'bb-tier--baseline' : 'bb-tier--discounted',
        ];
        if (isActive) tierClassList.push('is-active');
        if (isBest && !isBaseline) tierClassList.push('bb-tier--best');

        html += `<li class="${tierClassList.join(' ')}" data-tier-index="${i}" role="radio" aria-checked="${isActive}" tabindex="0">`;

        // "MOST POPULAR" badge — top-LEFT corner. Only on discounted tiers
        // (a baseline can't be a "best deal"). Sits opposite the save pill
        // so the two read as separate signals.
        if (isBest && !isBaseline) {
          html += `<span class="bb-tier__badge">Most Popular</span>`;
        }

        // "SAVE X%" promo pill — top-RIGHT corner. Discounted tiers only.
        // The promotional accent that makes each option determinable at a
        // glance ("which one saves me the most?").
        if (!isBaseline && savingsLabel) {
          html += `<span class="bb-tier__save-pill">${this.escapeHtml(savingsLabel)}</span>`;
        }

        html += `<div class="bb-tier__radio" aria-hidden="true"></div>`;
        html += `<div class="bb-tier__content">`;
        html += `<div class="bb-tier__label">${this.escapeHtml(this.getTierLabel(tier))}</div>`;
        const desc = this.getTierDescription(tier);
        if (desc) html += `<div class="bb-tier__desc">${this.escapeHtml(desc)}</div>`;
        html += `</div>`;

        html += `<div class="bb-tier__pricing">`;
        if (isBaseline) {
          // Baseline: plain neutral total. No red, no strikethrough,
          // no "per piece" repeat — the tier IS the unit.
          html += `<span class="bb-tier__price-total">${this.formatMoney(tp.originalTotal)}</span>`;
        } else {
          // Discounted: sale-red total + struck retail.
          html += `<span class="bb-tier__price-sale">${this.formatMoney(tp.discountedTotal)}</span>`;
          html += `<span class="bb-tier__price-original">${this.formatMoney(tp.originalTotal)}</span>`;
        }
        html += `</div>`;

        html += `</li>`;
      });
      html += `</ul>`;
      html += `<div class="bb-divider"></div>`;

      html += `<div class="bb-bottom">`;

      // Summary banner under the tier list — emphasizes the *dollar*
      // savings of the currently-selected tier (the tier card itself
      // shows the percent via the SAVE-X% pill, so this complements it).
      // Hidden entirely on baseline / no-savings tiers so the bottom of
      // the widget stays clean instead of showing an empty box.
      html += `<div class="bb-savings"${pricing.savings <= 0 ? ' hidden' : ''}>`;
      html += `<svg class="bb-savings__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="5" x2="5" y2="19"/><circle cx="6.5" cy="6.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>`;
      html += `<span class="bb-savings__text">You save <span class="bb-savings__amount">${this.formatMoney(pricing.savings)}</span></span>`;
      html += `</div>`;

      html += `<div class="bb-add-btn"><button type="button" class="btn btn--primary">`;
      html += `<span class="bb-add-btn__text">Add to Cart \u2014 ${this.formatMoney(pricing.discountedTotal)}</span>`;
      html += `<span class="bb-add-btn__spinner"></span>`;
      html += `</button></div>`;

      html += `<div class="bb-trust">`;
      html += `<span class="bb-trust__item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg> Secure Checkout</span>`;
      html += `<span class="bb-trust__item"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" width="14" height="14"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> Premium Quality</span>`;
      html += `</div>`;

      html += `</div>`;

      html += `<div class="bb-error" hidden></div>`;
      html += `</div>`;

      this.innerHTML = html;
      this.removeAttribute('hidden');
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll('.bb-tier').forEach((el) => {
        const handler = () => {
          const idx = parseInt(el.dataset.tierIndex, 10);
          if (idx === this.activeTierIndex) return;
          this.activeTierIndex = idx;
          this.updateUI();
        };
        el.addEventListener('click', handler);
        el.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            handler();
          }
        });
      });

      const addBtn = this.querySelector('.bb-add-btn button');
      if (addBtn) {
        addBtn.addEventListener('click', () => this.addToCart());
      }
    }

    updateUI() {
      const activeTier = this.tiers[this.activeTierIndex];
      const pricing = this.getTierPricing(activeTier);

      this.querySelectorAll('.bb-tier').forEach((el) => {
        const idx = parseInt(el.dataset.tierIndex, 10);
        const isActive = idx === this.activeTierIndex;
        el.classList.toggle('is-active', isActive);
        el.setAttribute('aria-checked', String(isActive));
      });

      const savingsEl = this.querySelector('.bb-savings');
      if (savingsEl) {
        if (pricing.savings > 0) {
          savingsEl.hidden = false;
          const amountEl = savingsEl.querySelector('.bb-savings__amount');
          if (amountEl) amountEl.textContent = this.formatMoney(pricing.savings);
        } else {
          savingsEl.hidden = true;
        }
      }

      const addBtn = this.querySelector('.bb-add-btn button');
      if (addBtn) {
        const textEl = addBtn.querySelector('.bb-add-btn__text');
        if (textEl) {
          textEl.textContent = `Add to Cart \u2014 ${this.formatMoney(pricing.discountedTotal)}`;
        }
      }
    }

    getActiveVariantId() {
      const mainForm = document.querySelector(`form[action="/cart/add"] input[name="id"]`);
      if (mainForm && mainForm.value) return parseInt(mainForm.value, 10);
      return this.variantId;
    }

    async applyDiscountCode() {
      if (!this.discountCode || this.discountApplied) return;
      this.discountApplied = true;
      try {
        await fetch(`/discount/${encodeURIComponent(this.discountCode)}`, {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch {
        this.discountApplied = false;
      }
    }

    async addToCart() {
      const activeTier = this.tiers[this.activeTierIndex];
      const pricing = this.getTierPricing(activeTier);

      const addBtn = this.querySelector('.bb-add-btn button');
      const errorEl = this.querySelector('.bb-error');
      if (errorEl) errorEl.hidden = true;

      if (addBtn) {
        addBtn.classList.add('is-loading');
        addBtn.disabled = true;
      }

      if (this.discountCode && pricing.savings > 0) {
        await this.applyDiscountCode();
      }

      const currentVariantId = this.getActiveVariantId();

      const items = [{
        id: currentVariantId,
        quantity: activeTier.qty,
        properties: {
          _buybox: 'true',
          _buybox_tier: this.getTierLabel(activeTier),
        },
      }];

      const cartDrawer = document.querySelector('cart-drawer');
      const isCartPage = document.body.classList.contains('cart-template');
      const isDrawer = window.FoxTheme && FoxTheme.settings && FoxTheme.settings.cartType === 'drawer';

      if (isCartPage || !isDrawer) {
        FoxTheme.utils.postLink(FoxTheme.routes.cart_add_url, {
          parameters: { items },
        });
        return;
      }

      let sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        section_url: window.location.pathname,
      });

      try {
        const response = await fetch(FoxTheme.routes.cart_add_url, {
          ...FoxTheme.utils.fetchConfig('javascript'),
          body,
        });
        const parsed = await response.json();

        if (parsed.status) {
          if (errorEl) {
            errorEl.textContent = parsed.description || 'Could not add to cart.';
            errorEl.hidden = false;
          }
          return;
        }

        const cartResp = await fetch(FoxTheme.routes.cart_url, {
          ...FoxTheme.utils.fetchConfig(),
        });
        const cartJson = await cartResp.json();
        cartJson['sections'] = parsed['sections'];

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(
          new CustomEvent('product-ajax:added', { detail: { product: parsed } })
        );

        if (cartDrawer) cartDrawer.show(addBtn);
      } catch (err) {
        console.error('BuyboxWidget: add to cart error', err);
        if (errorEl) {
          errorEl.textContent = 'Something went wrong. Please try again.';
          errorEl.hidden = false;
        }
      } finally {
        if (addBtn) {
          addBtn.classList.remove('is-loading');
          addBtn.disabled = false;
        }
      }
    }

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }
  }

  customElements.define('buybox-widget', BuyboxWidget);
}
